import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChartBar, FileXls, FilePdf } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

const PIE_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];
const METHOD_LABELS = { efectivo: "Efectivo", tarjeta_debito: "T. Débito", tarjeta_credito: "T. Crédito" };

export function POSReports() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = `${API}/pos/reports/sales`;
      const params = [];
      if (dateFrom) params.push(`date_from=${dateFrom}`);
      if (dateTo) params.push(`date_to=${dateTo}`);
      if (params.length) url += "?" + params.join("&");
      const res = await axios.get(url, { headers });
      setData(res.data);
    } catch { toast.error("Error al cargar reporte"); }
    setLoading(false);
  };

  useEffect(() => { fetchReport(); }, [token]);

  const methodData = data ? Object.entries(data.by_method || {}).map(([k, v]) => ({ name: METHOD_LABELS[k] || k, value: v })) : [];

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="pos-reports">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ChartBar size={24} weight="duotone" className="text-blue-600" /> Reportes de Ventas</h1>
          <p className="text-sm text-slate-500">Analisis de ventas por periodo, vendedor y producto</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 bg-white border rounded-xl p-4">
        <div>
          <label className="text-[10px] font-semibold text-slate-500 uppercase">Desde</label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-sm mt-1 w-40" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-500 uppercase">Hasta</label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-sm mt-1 w-40" />
        </div>
        <Button size="sm" className="h-8" onClick={fetchReport}>Consultar</Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border rounded-xl p-4 text-center">
          <p className="text-3xl font-extrabold text-slate-900">{data.total_sales}</p>
          <p className="text-[10px] font-semibold text-slate-500 uppercase mt-1">Total Ventas</p>
        </div>
        <div className="bg-white border rounded-xl p-4 text-center">
          <p className="text-3xl font-extrabold text-emerald-700 font-mono">${data.grand_total.toLocaleString("es-MX")}</p>
          <p className="text-[10px] font-semibold text-slate-500 uppercase mt-1">Monto Total</p>
        </div>
        <div className="bg-white border rounded-xl p-4 text-center">
          <p className="text-3xl font-extrabold text-blue-700 font-mono">${data.avg_sale.toLocaleString("es-MX")}</p>
          <p className="text-[10px] font-semibold text-slate-500 uppercase mt-1">Promedio por Venta</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Daily chart */}
        <div className="bg-white border rounded-xl p-5">
          <h3 className="text-xs font-semibold text-slate-500 uppercase mb-4">Ventas por Dia</h3>
          {data.daily.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => `$${v.toLocaleString("es-MX")}`} />
                <Bar dataKey="total" fill="#2563EB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-10">Sin datos</p>}
        </div>

        {/* Payment pie */}
        <div className="bg-white border rounded-xl p-5">
          <h3 className="text-xs font-semibold text-slate-500 uppercase mb-4">Distribucion por Metodo de Pago</h3>
          {methodData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width={180} height={180}>
                <PieChart>
                  <Pie data={methodData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {methodData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `$${v.toLocaleString("es-MX")}`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {methodData.map((m, i) => (
                  <div key={m.name} className="flex items-center gap-2 text-xs">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="text-slate-700 font-medium">{m.name}</span>
                    <span className="font-mono font-bold text-slate-900">${m.value.toLocaleString("es-MX")}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-sm text-slate-400 text-center py-10">Sin datos</p>}
        </div>

        {/* Top products */}
        <div className="bg-white border rounded-xl p-5">
          <h3 className="text-xs font-semibold text-slate-500 uppercase mb-4">Top Productos</h3>
          <div className="space-y-2">
            {data.top_products.slice(0, 10).map((p, i) => {
              const max = Math.max(...data.top_products.map(x => x.total), 1);
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-5 text-[10px] font-bold text-slate-400 text-right shrink-0">{i + 1}</span>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-800 truncate">{p.name}</p>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1"><div className="h-full rounded-full bg-blue-500" style={{ width: `${(p.total / max) * 100}%` }} /></div>
                  </div>
                  <span className="text-xs font-mono font-bold text-slate-900 shrink-0">${p.total.toLocaleString("es-MX")}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* By seller */}
        <div className="bg-white border rounded-xl p-5">
          <h3 className="text-xs font-semibold text-slate-500 uppercase mb-4">Ventas por Vendedor</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left text-xs font-semibold text-slate-600 py-2">Vendedor</th>
                <th className="text-center text-xs font-semibold text-slate-600 py-2">Ventas</th>
                <th className="text-right text-xs font-semibold text-slate-600 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.by_seller.map((v, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-2 text-xs font-medium text-slate-800">{v.name}</td>
                  <td className="py-2 text-xs text-center font-bold">{v.count}</td>
                  <td className="py-2 text-xs text-right font-mono font-bold text-emerald-700">${v.total.toLocaleString("es-MX")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
